import { useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'

interface DeleteConfirmModalProps {
    isOpen: boolean
    fileName: string
    onClose: () => void
    onConfirm: () => void
}

export function DeleteConfirmModal({
    isOpen,
    fileName,
    onClose,
    onConfirm
}: DeleteConfirmModalProps) {
    const [confirmText, setConfirmText] = useState('')
    const expectedText = 'DELETAR'
    const isConfirmValid = confirmText === expectedText

    if (!isOpen) return null

    const handleConfirm = () => {
        if (isConfirmValid) {
            onConfirm()
            setConfirmText('')
            onClose()
        }
    }

    const handleClose = () => {
        setConfirmText('')
        onClose()
    }

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                        <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">
                            Confirmar Exclusão
                        </h3>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded transition-colors"
                    >
                        <X className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4">
                    <div className="mb-4">
                        <p className="text-gray-700 dark:text-gray-300 mb-2">
                            Você está prestes a deletar o arquivo:
                        </p>
                        <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded border border-gray-300 dark:border-gray-600">
                            <code className="text-sm font-mono text-gray-900 dark:text-gray-100">
                                {fileName}
                            </code>
                        </div>
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50 rounded-lg p-3 mb-4">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                            ⚠️ Esta ação não pode ser desfeita!
                        </p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                            O arquivo será permanentemente removido do sistema.
                        </p>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Para confirmar, digite <span className="font-bold text-red-600 dark:text-red-400">DELETAR</span>:
                        </label>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                            placeholder="Digite DELETAR para confirmar"
                            autoFocus
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                        {confirmText && !isConfirmValid && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                Texto incorreto. Digite exatamente "DELETAR"
                            </p>
                        )}
                    </div>

                    {/* Buttons */}
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={!isConfirmValid}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed rounded-md transition-colors"
                        >
                            Deletar Permanentemente
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
